import BottomSheet from '@components/BottomSheet'
import SearchBar from '@components/SearchBar/SearchBar'
import Touchable from '@components/Touchable'
import { useCurrentWallet, useUserTokens } from '@plug-core/state'
import useConfig from '@plug-core/hooks/useConfig'
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Switch,
} from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMiniSquaresPlus } from 'react-icons/hi2'
import CustomTokenForm from './CustomTokenForm'
import { PlugToken, ChainOption } from '@plug-core/interfaces/common'
import { ICP_ICP_ID } from '@plug-core/constants/tokenIds'
import { SUPPORTED_CHAINS } from '@components/ChainPicker'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import TokenAvatar from '@components/TokenAvatar'

export const TokenManager = () => {
  const { toggleToken, isTokenAdded } = useUserTokens()
  const { currentWallet } = useCurrentWallet()
  const { tokens } = useConfig()
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false)
  const [chainMode, setChainMode] = useState<ChainOption>('all')
  const [filteredTokens, setFilteredTokens] = useState(tokens)

  const isTokenSelected = (t: PlugToken) => isTokenAdded(currentWallet, t)

  const renderToken = (token: PlugToken) => {
    return (
      <Stack key={token.id}>
        <Paper
          key={token.symbol}
          sx={{ paddingY: 0, minHeight: 50 }}
          elevation={0}
          component={ListItem}
        >
          <ListItemAvatar>
            <Stack position="relative">
              <TokenAvatar logo={token.logo} size={38} />
              <Stack
                position="absolute"
                right={13}
                bottom={-5}
                bgcolor="background.paper"
                padding={0.45}
                borderRadius={4}
              >
                <img src={getChainIcon(token.chain)} height={14} width={14} />
              </Stack>
            </Stack>
          </ListItemAvatar>
          <ListItemText
            primary={token.name}
            secondary={token.symbol}
            primaryTypographyProps={{ lineHeight: '18px', fontSize: 14 }}
            secondaryTypographyProps={{ lineHeight: '18px', fontSize: 14 }}
          />
          <ListItemSecondaryAction>
            <Switch
              disabled={token.id === ICP_ICP_ID}
              onChange={() => toggleToken(currentWallet, token.id)}
              checked={isTokenSelected(token)}
              color="success"
            />
          </ListItemSecondaryAction>
        </Paper>
      </Stack>
    )
  }

  const filterTokens = (newSearchTerm: string, newChain: ChainOption) => {
    let filtered = tokens

    if (newChain !== 'all') {
      filtered = filtered.filter((t) => t.chain === newChain)
    }

    const matchTerm = newSearchTerm.toLowerCase()
    if (matchTerm) {
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(matchTerm) || t.symbol.toLowerCase().includes(matchTerm)
      )
    }

    setFilteredTokens(filtered)
  }

  const onSearchTermChanged = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
    filterTokens(newSearchTerm, chainMode)
  }

  const onChainChanged = (newChain: ChainOption) => {
    setChainMode(newChain)
    filterTokens(searchTerm, newChain)
  }

  return (
    <Stack spacing={1.5} px={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box flex={1}>
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={onSearchTermChanged}
            placeholder={t('common.enterTokenName')}
            autoFocus
          />
        </Box>

        <Touchable onClick={() => setBottomSheetVisible(true)}>
          <Stack
            bgcolor={(theme) => theme.palette.background.paper}
            height={46}
            width={46}
            justifyContent="center"
            alignItems="center"
            borderRadius={1}
          >
            <HiMiniSquaresPlus size={20} />
          </Stack>
        </Touchable>
      </Stack>

      <Stack flexDirection="row">
        {['all' as ChainOption, ...SUPPORTED_CHAINS].map((chain) => {
          const isSelected = chain === chainMode

          return (
            <Box
              px={1}
              py={0.5}
              fontSize={13}
              borderRadius={1.5}
              key={chain}
              sx={{
                mr: 0.5,
                bgcolor: isSelected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(207, 209, 255, 15%)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Touchable
                onClick={() => onChainChanged(chain)}
                scaleFactor={0.08}
                style={{ opacity: 1, color: isSelected ? 'black' : 'white' }}
              >
                {t(`chains.short.${chain}`)}
              </Touchable>
            </Box>
          )
        })}
      </Stack>

      <Stack spacing={1} pt={0} component={List}>
        {filteredTokens.map(renderToken)}
      </Stack>

      <BottomSheet
        visible={bottomSheetVisible}
        onBackdropClick={() => setBottomSheetVisible(false)}
      >
        <CustomTokenForm hideForm={() => setBottomSheetVisible(false)} />
      </BottomSheet>
    </Stack>
  )
}
